/**
 * Blog Post 18: How to Read Your Mortgage Statement in Hong Kong
 * 香港按揭月結單全解讀：每個數字代表甚麼？
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function BlogPost18() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    useEffect(() => {
        trackPageView('/blog/how-to-read-mortgage-statement-hong-kong', 'Blog Post 18');
        document.title = isEnglish
            ? 'How to Read Your Mortgage Statement in Hong Kong | PayWise365'
            : '香港按揭月結單全解讀：每個數字代表甚麼？ | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'How to Read Your Mortgage Statement in Hong Kong | PayWise365'
        : '香港按揭月結單全解讀：每個數字代表甚麼？ | PayWise365';

    const seoDescription = isEnglish
        ? 'Learn how to read and understand every line on your Hong Kong mortgage statement. Covers principal, interest breakdown, outstanding balance, penalty clauses, and what to check each month.'
        : '學識閱讀香港按揭月結單每一行數字。包括本金、利息分拆、未償還結餘、罰息條款，以及每月應該檢查的重點。';

    const seoKeywords = isEnglish
        ? 'mortgage statement Hong Kong, read mortgage statement, mortgage principal interest, outstanding balance, mortgage penalty, 按揭月結單, monthly repayment breakdown, mortgage HK 2026'
        : '按揭月結單, 供樓月結單, 本金利息分拆, 未償還貸款, 按揭罰息, 每月還款明細, 香港按揭 2026';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://www.paywise365.com/blog/how-to-read-mortgage-statement-hong-kong"
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-04-12T00:00:00+08:00"
                modifiedDate="2026-04-12T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Mortgage Guide' : '按揭指南'}
                tags={isEnglish
                    ? ['Mortgage Statement', 'Monthly Repayment', 'Hong Kong Mortgage', 'Personal Finance']
                    : ['按揭月結單', '每月供款', '香港按揭', '個人理財']
                }
            />
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                    color: 'white',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ mb: 3, fontSize: '14px', opacity: 0.9 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Home' : '首頁'}
                        </a>
                        {' > '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Blog' : '資訊'}
                        </a>
                        {' > '}
                        <span style={{ opacity: 0.8 }}>
                            {isEnglish ? 'Mortgage Statement' : '按揭月結單'}
                        </span>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'How to Read Your Mortgage Statement in Hong Kong'
                            : '香港按揭月結單全解讀：每個數字代表甚麼？'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                        {isEnglish
                            ? 'Understand every number on your statement and catch costly errors early'
                            : '了解月結單上每個數字，及早發現費用錯誤'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box
                    component="article"
                    sx={{
                        lineHeight: 1.8,
                        '& p': { mb: 2 },
                        '& h2': { mt: 4, mb: 2, fontSize: '1.75rem', fontWeight: 700 },
                        '& h3': { mt: 3, mb: 1.5, fontSize: '1.5rem', fontWeight: 600 },
                        '& ul, & ol': { mb: 2, pl: 3 },
                        '& li': { mb: 1 },
                    }}
                >
                    {!isEnglish ? (
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#ecfdf5',
                                        color: '#0891b2',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>按揭指南</span>
                                    <span>發布日期：2026年4月12日</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>閱讀時間：5 分鐘</span>
                                </div>
                            </div>

                            <h2>點解要睇得明按揭月結單？</h2>
                            <p>
                                大部分香港業主每個月都會收到銀行寄嚟嘅按揭月結單，但好多人只係睇一睇每月供款金額，就放埋一邊。其實月結單上面嘅資訊非常重要——唔單止可以幫你了解還款進度，仲可以幫你及早發現銀行計錯數或者收多咗費用。
                            </p>
                            <p>
                                根據金管局數據，香港有超過60萬宗未償還按揭貸款。假設每100宗有1宗出現計算差異，受影響嘅業主數以千計。所以學識睇月結單，絕對值得花5分鐘時間。
                            </p>

                            <h2>月結單上的7個關鍵數字</h2>

                            <h3>1. 每月供款金額（Monthly Instalment）</h3>
                            <p>
                                呢個係你每個月固定要俾銀行嘅金額，包括<strong>本金</strong>同<strong>利息</strong>兩部分。如果你用P按或H按，呢個數字可能每個月都唔同。
                            </p>
                            <ul>
                                <li><strong>P按（最優惠利率按揭）：</strong>當銀行調整P rate，你嘅供款會跟住變</li>
                                <li><strong>H按（同業拆息按揭）：</strong>每個月根據HIBOR浮動，但有封頂息率保障</li>
                            </ul>

                            <h3>2. 本金部分（Principal Repaid）</h3>
                            <p>
                                呢個數字代表你今個月實際還咗幾多錢嘅「真正欠款」。供樓初期，本金部分佔比較少，利息佔大部分。隨住時間推移，本金比例會越嚟越高。
                            </p>
                            <p>
                                <strong>例子：</strong>假設你每月供HK$20,000。第一年，可能只有HK$7,000係本金，HK$13,000係利息。到第十五年，本金可能升到HK$14,000，利息降到HK$6,000。
                            </p>

                            <h3>3. 利息部分（Interest Charged）</h3>
                            <p>
                                銀行根據你嘅<strong>未償還本金 × 年利率 ÷ 12</strong>計算每月利息。呢個數字應該同你嘅實際利率一致。你可以用以下公式驗證：
                            </p>
                            <div style={{
                                background: '#f0fdfa',
                                border: '1px solid #99f6e4',
                                borderRadius: '8px',
                                padding: '16px 20px',
                                margin: '16px 0',
                                fontFamily: 'monospace',
                            }}>
                                利息 = 未償還本金 × 年利率 ÷ 12<br />
                                例：HK$4,000,000 × 3.875% ÷ 12 = HK$12,917
                            </div>

                            <h3>4. 未償還本金結餘（Outstanding Principal Balance）</h3>
                            <p>
                                呢個係你目前仲欠銀行幾多錢。每個月呢個數字都應該減少（減少嘅金額就係上面嘅「本金部分」）。如果你有做過<strong>部分提前還款（Partial Prepayment）</strong>，呢個數字會明顯下跌。
                            </p>

                            <h3>5. 實際年利率（Effective Interest Rate）</h3>
                            <p>
                                銀行會列出你目前嘅按揭利率。如果你用H按，注意呢個利率每個月可能唔同。你應該將呢個利率同銀行最初批出嘅offer letter做比較，確保冇出入。
                            </p>
                            <ul>
                                <li>P按例子：P - 1.75% = 5.875% - 1.75% = <strong>4.125%</strong></li>
                                <li>H按例子：1M HIBOR + 1.30% = 2.50% + 1.30% = <strong>3.80%</strong>（受封頂限制）</li>
                            </ul>

                            <h3>6. 剩餘還款期（Remaining Loan Tenor）</h3>
                            <p>
                                你仲有幾多個月要供。30年按揭 = 360個月。每次檢查月結單，確認呢個數字有減少。
                            </p>

                            <h3>7. 罰息期狀態</h3>
                            <p>
                                部分月結單會顯示你是否仍然喺罰息期內。大部分銀行嘅罰息期係1至3年。如果你打算轉按或提前還款，呢個資訊非常重要——違反罰息期可能要繳付貸款額1%至3%嘅罰款。
                            </p>

                            <h2>每月檢查清單</h2>
                            <p>收到月結單後，花2分鐘做以下檢查：</p>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                margin: '16px 0',
                            }}>
                                <p style={{ marginBottom: '8px' }}>✅ 供款金額同上個月比較有冇異常變動</p>
                                <p style={{ marginBottom: '8px' }}>✅ 利率同你嘅按揭合約一致</p>
                                <p style={{ marginBottom: '8px' }}>✅ 未償還本金有減少（對比上月）</p>
                                <p style={{ marginBottom: '8px' }}>✅ 冇出現任何未經授權嘅收費</p>
                                <p style={{ marginBottom: '0' }}>✅ 如有部分還款，金額已正確反映</p>
                            </div>

                            <h2>發現錯誤點算？</h2>
                            <p>
                                如果你發現月結單有任何異常，應該立即採取以下步驟：
                            </p>
                            <ol>
                                <li><strong>記錄差異：</strong>用截圖或影印保留證據</li>
                                <li><strong>聯絡銀行：</strong>致電按揭部門查詢，記低通話時間同對方姓名</li>
                                <li><strong>書面跟進：</strong>如果電話解決唔到，寫正式投訴信</li>
                                <li><strong>向金管局投訴：</strong>如銀行未能合理處理，可向香港金融管理局投訴</li>
                            </ol>

                            <h2>實用小貼士</h2>
                            <ul>
                                <li>每月將月結單嘅關鍵數字記錄喺Excel或Google Sheets，方便追蹤趨勢</li>
                                <li>利用PayWise365嘅<a href="/" style={{ color: '#0891b2' }}>按揭計算機</a>驗證銀行嘅利息計算是否正確</li>
                                <li>設定提醒，喺罰息期結束前3個月開始比較轉按選項</li>
                                <li>保留最少最近12個月嘅月結單副本</li>
                            </ul>

                            <div style={{
                                background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)',
                                border: '1px solid #99f6e4',
                                borderRadius: '12px',
                                padding: '24px',
                                margin: '32px 0',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0891b2', mb: 1 }}>
                                    總結
                                </Typography>
                                <p style={{ marginBottom: 0 }}>
                                    按揭月結單唔只係一張「賬單」——佢係你管理最大筆負債嘅重要工具。每個月花2分鐘檢查，可以幫你節省以千計嘅冤枉錢，同時更清楚自己嘅還款進度。如果你想即時計算你嘅按揭分期，使用我哋嘅免費計算機工具啦。
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#ecfdf5',
                                        color: '#0891b2',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>Mortgage Guide</span>
                                    <span>Published: April 12, 2026</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>5 min read</span>
                                </div>
                            </div>

                            <h2>Why You Should Understand Your Mortgage Statement</h2>
                            <p>
                                Most Hong Kong homeowners receive a monthly mortgage statement from their bank, yet many only glance at the repayment amount before filing it away. Your statement actually contains critical information — it helps you track your repayment progress, verify the bank's calculations, and catch overcharges early.
                            </p>
                            <p>
                                According to the HKMA, there are over 600,000 outstanding mortgage loans in Hong Kong. Even if calculation discrepancies affect just 1 in 100 cases, thousands of homeowners could be overpaying. Learning to read your statement is well worth 5 minutes.
                            </p>

                            <h2>The 7 Key Numbers on Your Statement</h2>

                            <h3>1. Monthly Instalment Amount</h3>
                            <p>
                                This is the fixed amount you pay the bank each month, comprising <strong>principal</strong> and <strong>interest</strong>. If you have a P-rate or H-rate mortgage, this figure may change monthly.
                            </p>
                            <ul>
                                <li><strong>P-rate mortgage:</strong> Your payment adjusts when the bank changes its Prime rate</li>
                                <li><strong>H-rate mortgage:</strong> Fluctuates with HIBOR each month, but protected by a cap rate</li>
                            </ul>

                            <h3>2. Principal Repaid</h3>
                            <p>
                                This shows how much of your payment actually went toward reducing your debt. In the early years, the principal portion is small while interest dominates. Over time, the ratio shifts in your favour.
                            </p>
                            <p>
                                <strong>Example:</strong> On a HK$20,000 monthly payment, Year 1 might split as HK$7,000 principal and HK$13,000 interest. By Year 15, it could be HK$14,000 principal and HK$6,000 interest.
                            </p>

                            <h3>3. Interest Charged</h3>
                            <p>
                                The bank calculates this as <strong>Outstanding Principal × Annual Rate ÷ 12</strong>. You can verify this yourself:
                            </p>
                            <div style={{
                                background: '#f0fdfa',
                                border: '1px solid #99f6e4',
                                borderRadius: '8px',
                                padding: '16px 20px',
                                margin: '16px 0',
                                fontFamily: 'monospace',
                            }}>
                                Interest = Outstanding Balance × Annual Rate ÷ 12<br />
                                E.g.: HK$4,000,000 × 3.875% ÷ 12 = HK$12,917
                            </div>

                            <h3>4. Outstanding Principal Balance</h3>
                            <p>
                                This is how much you still owe. It should decrease every month by the principal repaid amount. If you've made a <strong>partial prepayment</strong>, you'll see a noticeable drop.
                            </p>

                            <h3>5. Effective Interest Rate</h3>
                            <p>
                                Your current mortgage rate, which you should cross-check against your original offer letter. For H-rate mortgages, this changes monthly.
                            </p>
                            <ul>
                                <li>P-rate example: P - 1.75% = 5.875% - 1.75% = <strong>4.125%</strong></li>
                                <li>H-rate example: 1M HIBOR + 1.30% = 2.50% + 1.30% = <strong>3.80%</strong> (subject to cap)</li>
                            </ul>

                            <h3>6. Remaining Loan Tenor</h3>
                            <p>
                                The number of months left on your mortgage. A 30-year loan means 360 months. Confirm this decreases each month.
                            </p>

                            <h3>7. Penalty Period Status</h3>
                            <p>
                                Some statements indicate whether you're still within the penalty (clawback) period, typically 1–3 years. This is crucial if you're considering refinancing or early repayment — breaking the penalty period could cost 1%–3% of the loan amount.
                            </p>

                            <h2>Monthly Checklist</h2>
                            <p>Spend 2 minutes running through this after receiving your statement:</p>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                margin: '16px 0',
                            }}>
                                <p style={{ marginBottom: '8px' }}>✅ Payment amount hasn't changed unexpectedly</p>
                                <p style={{ marginBottom: '8px' }}>✅ Interest rate matches your mortgage contract</p>
                                <p style={{ marginBottom: '8px' }}>✅ Outstanding balance has decreased from last month</p>
                                <p style={{ marginBottom: '8px' }}>✅ No unauthorised fees or charges</p>
                                <p style={{ marginBottom: '0' }}>✅ Any partial prepayments are correctly reflected</p>
                            </div>

                            <h2>What to Do If You Find an Error</h2>
                            <ol>
                                <li><strong>Document the discrepancy:</strong> Screenshot or photocopy the statement</li>
                                <li><strong>Contact the bank:</strong> Call the mortgage department and note the time and representative's name</li>
                                <li><strong>Follow up in writing:</strong> If not resolved by phone, submit a formal complaint letter</li>
                                <li><strong>Escalate to HKMA:</strong> If the bank doesn't resolve the issue, file a complaint with the Hong Kong Monetary Authority</li>
                            </ol>

                            <h2>Practical Tips</h2>
                            <ul>
                                <li>Record your key statement figures monthly in a spreadsheet to track trends</li>
                                <li>Use PayWise365's <a href="/" style={{ color: '#0891b2' }}>mortgage calculators</a> to verify the bank's interest calculations</li>
                                <li>Set a reminder 3 months before your penalty period ends to start comparing refinancing options</li>
                                <li>Keep at least the last 12 months of statements on file</li>
                            </ul>

                            <div style={{
                                background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)',
                                border: '1px solid #99f6e4',
                                borderRadius: '12px',
                                padding: '24px',
                                margin: '32px 0',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0891b2', mb: 1 }}>
                                    Key Takeaway
                                </Typography>
                                <p style={{ marginBottom: 0 }}>
                                    Your mortgage statement isn't just a bill — it's a vital tool for managing your largest financial obligation. Two minutes of checking each month can save you thousands in overcharges and keep your repayment firmly on track. Try our free calculators to instantly verify your numbers.
                                </p>
                            </div>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
